import './utils/polyfills';
import './assets/styles/elevio.css';
import './assets/styles/intl-tel-input.css';
import './assets/styles/recaptcha.css';
import './assets/flags/flags.min.css';

// Make sure sentry gets set up as early as possible:
import '@hydra/utils/sentry';

import React, { useEffect } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { createBrowserHistory } from 'history';
import { configureUrlQuery } from 'react-url-query';
import { StylesProvider } from '@material-ui/core/styles';

import { I18nContext, localizeNumeral, localizeMoment, translateTag } from '@hydra/common/components/i18n';
import { FeatureFlagsProvider } from '@hydra/common/components/FeatureFlags/featureFlagContext';
import { UserProvider, ErrorProvider } from '@hydra/common/context';
import createRoutes from './routes';
import settings from '@hydra/utils/settings';

import {
  createRender,
  createFarceRouter,
  createInitialBrowserRouter
} from 'found';
import { BrowserProtocol, queryMiddleware } from 'farce';

import { Resolver } from 'found-relay';
import { Environment, RecordSource, Store } from 'relay-runtime';

import createRelayNetwork from './routes/crawler/utils/network';

const { pathname, search, hash } = window.location;
const location = `${pathname}${search}${hash}`;

const { lang, user, translations, props, error } = window.__INITIAL_STATE__ || {};
const isIE10 = (window.navigator.appVersion.indexOf('MSIE 10') !== -1);
settings.add({ isIE10 });

const numeralWrapped = localizeNumeral(lang);
const momentWrapped = localizeMoment(lang);

const translate = (tag, options) => translateTag({ labels: translations.labels, tag, options });

const routeConfig = createRoutes();

const isCrawler = (location || '').match(/^\/[a-zA-Z]{2}\/crawler\//);

function afterRender () {
  // flag to signal synthetics tests
  window.appReady = true;
  const matuiSsrStyles = document.getElementById('matui-ssr-styles');
  if (matuiSsrStyles) {
    matuiSsrStyles.remove();
  }
}

const container = document.getElementById('content');

if (isCrawler) {
  const history = createBrowserHistory();
  configureUrlQuery({ history });

  const root = createRoot(container);

  root.render(<CrawlerApplication />);
} else {
  const matchContext = { props };
  (async () => {
    const BrowserRouter = await createInitialBrowserRouter({ routeConfig, matchContext });

    hydrateRoot(
      container,
      <HydratedApplication BrowserRouterComponent={BrowserRouter} />
    );
  })();
}

const HydratedApplication = ({ BrowserRouterComponent }) => {
  useEffect(() => {
    afterRender();
  }, []);

  return (
    <StylesProvider>
      <FeatureFlagsProvider>
        <I18nContext
          i18n={{
            translate,
            moment: momentWrapped,
            numeral: numeralWrapped,
            locale: lang,
            languageLabels: translations.languageLabels,
            location,
            pathname
          }}
        >
          <UserProvider user={user}>
            <ErrorProvider error={error}>
              <BrowserRouterComponent />
            </ErrorProvider>
          </UserProvider>
        </I18nContext>
      </FeatureFlagsProvider>
    </StylesProvider>
  );
};

function CrawlerApplication () {
  useEffect(() => {
    afterRender();
  }, []);

  const environment = new Environment({
    store: new Store(new RecordSource()),
    network: createRelayNetwork(pathname)
  });

  const Router = createFarceRouter({
    historyProtocol: new BrowserProtocol(),
    historyMiddlewares: [queryMiddleware],
    routeConfig,
    render: createRender({})
  });

  return (
    <FeatureFlagsProvider>
      <I18nContext
        i18n={{
          translate,
          moment: momentWrapped,
          numeral: numeralWrapped,
          locale: lang,
          languageLabels: translations.languageLabels,
          location,
          pathname
        }}
      >
        <UserProvider user={user}>
          <ErrorProvider error={error}>
            <Router resolver={new Resolver(environment)} />
          </ErrorProvider>
        </UserProvider>
      </I18nContext>
    </FeatureFlagsProvider>
  );
}
